import { motion } from "framer-motion";
import { useState } from "react";
import emailjs from "@emailjs/browser";
import {
    FaCalendarAlt,
    FaPhoneAlt,
    FaEnvelope,
    FaUser,
    FaBuilding,
    FaArrowRight
} from "react-icons/fa";

export default function ScheduleConsultation() {
    // 1. Define form state
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        company: "",
        service: "Select Service",
        date: "",
        timeSlot: "Select Time Slot",
        timeZone: "Time Zone",
        preferredMeeting: "Preferred Meeting",
        projectDetails: ""
    });

    // 2. Define loading and response state
    const [status, setStatus] = useState({ loading: false, success: null, error: null });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, success: null, error: null });

        // Basic validation
        if (!formData.fullName || !formData.email || !formData.date) {
            setStatus({
                loading: false,
                success: false,
                error: "Please fill in your name, email, and preferred date."
            });
            return;
        }

        // EmailJS Configuration
        const SERVICE_ID = "service_dmf83nl";
        const ADMIN_TEMPLATE_ID = "template_cxhl7f4";
        const USER_TEMPLATE_ID = "template_a30c598";
        const PUBLIC_KEY = "LyrCrgroJ266QMU-Y";

        try {
            // Send Notification Email to Admin
            const adminPromise = emailjs.send(
                SERVICE_ID,
                ADMIN_TEMPLATE_ID,
                {
                    to_name: "Admin",
                    from_name: formData.fullName,
                    user_email: formData.email,
                    phone: formData.phone,
                    company: formData.company,
                    service: formData.service,
                    date: formData.date,
                    time_slot: formData.timeSlot,
                    time_zone: formData.timeZone,
                    preferred_meeting: formData.preferredMeeting,
                    project_details: formData.projectDetails,
                },
                PUBLIC_KEY
            );

            // Send Confirmation Email to User
            const userPromise = emailjs.send(
                SERVICE_ID,
                USER_TEMPLATE_ID,
                {
                    to_name: formData.fullName,
                    user_email: formData.email,
                    service: formData.service,
                    date: formData.date,
                    time_slot: formData.timeSlot,
                    time_zone: formData.timeZone,
                    preferred_meeting: formData.preferredMeeting,
                },
                PUBLIC_KEY
            );

            // Trigger both emails
            await Promise.all([adminPromise, userPromise]);

            setStatus({
                loading: false,
                success: "Consultation successfully scheduled! Check your inbox for details.",
                error: null
            });

            // Clear the form fields
            setFormData({
                fullName: "",
                email: "",
                phone: "",
                company: "",
                service: "Select Service",
                date: "",
                timeSlot: "Select Time Slot",
                timeZone: "Time Zone",
                preferredMeeting: "Preferred Meeting",
                projectDetails: ""
            });

        } catch (err) {
            console.error("Email sending error:", err);
            setStatus({
                loading: false,
                success: false,
                error: "Failed to schedule. Please try again or reach out directly."
            });
        }
    };

    const inputStyle = {
        width: "100%",
        padding: "16px 18px",
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,.15)",
        background: "#111827",
        color: "#fff",
        fontSize: "16px",
        outline: "none",
        boxSizing: "border-box",
        appearance: "none",
        WebkitAppearance: "none",
        MozAppearance: "none",
        cursor: "pointer"
    };

    return (
        <>
            <style>{`
                /* Dropdown styling with crisp custom arrow */
                select {
                    background: #111827 !important;
                    color: white;
                    background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white' width='20px' height='20px'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E") !important;
                    background-repeat: no-repeat !important;
                    background-position: right 16px center !important;
                    padding-right: 40px !important;
                }
                select option {
                    background: #111827;
                    color: white;
                    padding: 10px;
                }
                select:focus {
                    border: 1px solid #3b82f6 !important;
                }

                /* Makes the dark browser date-picker calendar icon bright white */
                input[type="date"]::-webkit-calendar-picker-indicator {
                    filter: invert(1) brightness(1);
                    cursor: pointer;
                    opacity: 0.8;
                    transition: opacity 0.2s;
                }
                input[type="date"]::-webkit-calendar-picker-indicator:hover {
                    opacity: 1;
                }

                .schedule-page {
                    background:#060B17;
                    color:white;
                    overflow:hidden;
                }
                .schedule-hero {
                    padding:120px 8%;
                    position:relative;
                }
                .schedule-hero::before {
                    content:'';
                    position:absolute;
                    width:500px;
                    height:500px;
                    background:#2563eb30;
                    border-radius:50%;
                    filter:blur(120px);
                    top:-200px;
                    left:-200px;
                }
                .schedule-hero::after {
                    content:'';
                    position:absolute;
                    width:450px;
                    height:450px;
                    background:#06b6d430;
                    border-radius:50%;
                    filter:blur(120px);
                    right:-180px;
                    bottom:-150px;
                }
                .hero-content {
                    position:relative;
                    z-index:2;
                    max-width:750px;
                }
                .hero-badge {
                    display:inline-block;
                    background:#1d4ed8;
                    padding:10px 25px;
                    border-radius:30px;
                    margin-bottom:25px;
                    font-size:14px;
                }
                .hero-content h1 {
                    font-size:60px;
                    line-height:1.1;
                    margin-bottom:25px;
                }
                .hero-content p {
                    font-size:20px;
                    color:#cbd5e1;
                    line-height:1.8;
                }
                .hero-buttons {
                    margin-top:40px;
                    display:flex;
                    gap:20px;
                    flex-wrap:wrap;
                }
                .hero-btn {
                    background:#2563eb;
                    color:white;
                    padding:18px 35px;
                    border-radius:50px;
                    text-decoration:none;
                    transition:.3s;
                    display:inline-flex;
                    align-items:center;
                    gap:10px;
                }
                .hero-btn:hover {
                    transform:translateY(-5px);
                }
                .hero-outline {
                    border:2px solid #2563eb;
                    color:white;
                    background:transparent;
                }
                .hero-cards {
                    margin-top:70px;
                    display:grid;
                    grid-template-columns:repeat(3,1fr);
                    gap:30px;
                }
                .hero-card {
                    background:rgba(255,255,255,.05);
                    backdrop-filter:blur(20px);
                    border:1px solid rgba(255,255,255,.08);
                    border-radius:20px;
                    padding:35px;
                    transition:.4s;
                }
                .hero-card:hover {
                    transform:translateY(-10px);
                }
                .hero-card svg {
                    font-size:42px;
                    color:#38bdf8;
                    margin-bottom:20px;
                }
                .hero-card h3 {
                    margin-bottom:15px;
                }
                .hero-card p {
                    color:#cbd5e1;
                    line-height:1.8;
                }
                @media(max-width:900px){
                    .hero-content h1 {
                        font-size:42px;
                    }
                    .hero-cards {
                        grid-template-columns:1fr;
                    }
                }
            `}</style>

            <div className="schedule-page">
                <section className="schedule-hero">
                    <motion.div
                        className="hero-content"
                        initial={{ opacity: 0, y: 60 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: .8 }}
                    >
                        <span className="hero-badge">🚀 Free Consultation</span>
                        <h1>Let's Build Your Next Digital Solution Together</h1>
                        <p>
                            Schedule a free consultation with our NetSuite,
                            Salesforce and Web Development experts.
                            We'll understand your business requirements,
                            recommend the best solution, and provide a roadmap for success.
                        </p>
                        <div className="hero-buttons">
                            <a href="#booking" className="hero-btn">
                                Book Consultation <FaArrowRight />
                            </a>
                            <a href="#benefits" className="hero-btn hero-outline">
                                Learn More
                            </a>
                        </div>
                    </motion.div>

                    <div className="hero-cards">
                        <div className="hero-card">
                            <FaCalendarAlt />
                            <h3>Free 30 Minute Session</h3>
                            <p>Discuss your business requirements with our experts.</p>
                        </div>
                        <div className="hero-card">
                            <FaBuilding />
                            <h3>Business Analysis</h3>
                            <p>We analyze your existing process and recommend improvements.</p>
                        </div>
                        <div className="hero-card">
                            <FaPhoneAlt />
                            <h3>Personalized Guidance</h3>
                            <p>Receive expert recommendations tailored to your business.</p>
                        </div>
                    </div>
                </section>

                {/* ================= WHY BOOK ================= */}
                <section
                    id="benefits"
                    style={{
                        maxWidth: "1300px",
                        margin: "0 auto",
                        padding: "20px 8% 80px",
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        style={{ textAlign: "center", marginBottom: "60px" }}
                    >
                        <h2 style={{ fontSize: "42px", marginBottom: "20px" }}>
                            Why Schedule a Consultation?
                        </h2>
                        <p
                            style={{
                                color: "#CBD5E1",
                                maxWidth: "700px",
                                margin: "0 auto",
                                lineHeight: "1.8",
                            }}
                        >
                            Every business is unique. We understand your
                            requirements first and then recommend the right
                            NetSuite, Salesforce, or Web solution.
                        </p>
                    </motion.div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))",
                            gap: "30px",
                            marginBottom: "80px",
                        }}
                    >
                        {[
                            {
                                title: "Free Consultation",
                                desc: "Discuss your project without any obligation.",
                            },
                            {
                                title: "Solution Roadmap",
                                desc: "Receive implementation recommendations.",
                            },
                            {
                                title: "Technology Guidance",
                                desc: "Choose the right ERP, CRM or Web solution.",
                            },
                            {
                                title: "Project Estimation",
                                desc: "Understand timeline, budget and next steps.",
                            },
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ y: -8 }}
                                style={{
                                    background: "rgba(255,255,255,.05)",
                                    border: "1px solid rgba(255,255,255,.08)",
                                    borderRadius: "20px",
                                    padding: "35px",
                                    backdropFilter: "blur(15px)",
                                }}
                            >
                                <h3 style={{ marginBottom: "15px", color: "#38BDF8" }}>
                                    {item.title}
                                </h3>
                                <p style={{ color: "#CBD5E1", lineHeight: "1.8" }}>
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    {/* ================= FORM ================= */}
                    <motion.div
                        id="booking"
                        initial={{ opacity: 0, y: 60 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        style={{
                            background: "rgba(255,255,255,.05)",
                            border: "1px solid rgba(255,255,255,.08)",
                            borderRadius: "25px",
                            padding: "50px",
                            backdropFilter: "blur(20px)",
                        }}
                    >
                        <h2 style={{ textAlign: "center", fontSize: "40px", marginBottom: "10px" }}>
                            Schedule Your Consultation
                        </h2>
                        <p style={{ textAlign: "center", color: "#CBD5E1", marginBottom: "40px" }}>
                            Fill in the details below and our team will contact you within 24 hours.
                        </p>

                        <form onSubmit={handleSubmit}>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
                                    gap: "25px",
                                }}
                            >
                                <input
                                    type="text"
                                    name="fullName"
                                    placeholder="Full Name"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    style={inputStyle}
                                    required
                                />

                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email Address"
                                    value={formData.email}
                                    onChange={handleChange}
                                    style={inputStyle}
                                    required
                                />

                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Phone Number"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    style={inputStyle}
                                />

                                <input
                                    type="text"
                                    name="company"
                                    placeholder="Company Name"
                                    value={formData.company}
                                    onChange={handleChange}
                                    style={inputStyle}
                                />

                                <select
                                    name="service"
                                    value={formData.service}
                                    onChange={handleChange}
                                    style={inputStyle}
                                >
                                    <option disabled value="Select Service">Select Service</option>
                                    <option value="NetSuite ERP">NetSuite ERP</option>
                                    <option value="Salesforce CRM">Salesforce CRM</option>
                                    <option value="Web Development">Web Development</option>
                                </select>

                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    style={inputStyle}
                                    required
                                />

                                <select
                                    name="timeSlot"
                                    value={formData.timeSlot}
                                    onChange={handleChange}
                                    style={inputStyle}
                                >
                                    <option disabled value="Select Time Slot">Select Time Slot</option>
                                    <option value="09:00 AM">09:00 AM</option>
                                    <option value="09:30 AM">09:30 AM</option>
                                    <option value="10:00 AM">10:00 AM</option>
                                    <option value="10:30 AM">10:30 AM</option>
                                    <option value="11:00 AM">11:00 AM</option>
                                    <option value="11:30 AM">11:30 AM</option>
                                    <option value="12:00 PM">12:00 PM</option>
                                    <option value="12:30 PM">12:30 PM</option>
                                    <option value="01:00 PM">01:00 PM</option>
                                    <option value="01:30 PM">01:30 PM</option>
                                    <option value="02:00 PM">02:00 PM</option>
                                    <option value="02:30 PM">02:30 PM</option>
                                    <option value="03:00 PM">03:00 PM</option>
                                    <option value="03:30 PM">03:30 PM</option>
                                    <option value="04:00 PM">04:00 PM</option>
                                    <option value="04:30 PM">04:30 PM</option>
                                    <option value="05:00 PM">05:00 PM</option>
                                    <option value="05:30 PM">05:30 PM</option>
                                    <option value="06:00 PM">06:00 PM</option>
                                </select>

                                <select
                                    name="timeZone"
                                    value={formData.timeZone}
                                    onChange={handleChange}
                                    style={inputStyle}
                                >
                                    <option disabled value="Time Zone">Time Zone</option>
                                    <option value="IST (India Standard Time)">IST (India Standard Time)</option>
                                    <option value="EST (Eastern Standard Time)">EST (Eastern Standard Time)</option>
                                    <option value="CST (Central Standard Time)">CST (Central Standard Time)</option>
                                    <option value="MST (Mountain Standard Time)">MST (Mountain Standard Time)</option>
                                    <option value="PST (Pacific Standard Time)">PST (Pacific Standard Time)</option>
                                    <option value="GMT (Greenwich Mean Time)">GMT (Greenwich Mean Time)</option>
                                    <option value="UTC (Coordinated Universal Time)">UTC (Coordinated Universal Time)</option>
                                    <option value="AEST (Australian Eastern Standard Time)">AEST (Australian Eastern Standard Time)</option>
                                </select>

                                <select
                                    name="preferredMeeting"
                                    value={formData.preferredMeeting}
                                    onChange={handleChange}
                                    style={inputStyle}
                                >
                                    <option disabled value="Preferred Meeting">Preferred Meeting</option>
                                    <option value="Google Meet">Google Meet</option>
                                    <option value="Microsoft Teams">Microsoft Teams</option>
                                    <option value="Zoom">Zoom</option>
                                    {/* <option value="Phone Call">Phone Call</option> */}
                                </select>
                            </div>

                            <textarea
                                name="projectDetails"
                                value={formData.projectDetails}
                                onChange={handleChange}
                                rows="6"
                                placeholder="Tell us about your project..."
                                style={{
                                    ...inputStyle,
                                    width: "100%",
                                    marginTop: "25px",
                                    resize: "vertical",
                                }}
                            />

                            {/* Response status messages */}
                            {status.success && (
                                <p style={{ color: "#4ade80", textAlign: "center", marginTop: "20px" }}>
                                    {status.success}
                                </p>
                            )}
                            {status.error && (
                                <p style={{ color: "#f87171", textAlign: "center", marginTop: "20px" }}>
                                    {status.error}
                                </p>
                            )}

                            <div style={{ textAlign: "center", marginTop: "35px" }}>
                                <button
                                    type="submit"
                                    disabled={status.loading}
                                    style={{
                                        background: status.loading ? "#4b5563" : "#2563EB",
                                        color: "#fff",
                                        border: "none",
                                        padding: "18px 40px",
                                        borderRadius: "50px",
                                        fontSize: "17px",
                                        cursor: status.loading ? "not-allowed" : "pointer",
                                        transition: ".3s",
                                    }}
                                >
                                    {status.loading ? "Processing..." : "Schedule Consultation"}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </section>

                {/* ================= FAQ ================= */}
                <section style={{ maxWidth: "1200px", margin: "80px auto", padding: "0 8%" }}>
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        style={{ textAlign: "center", marginBottom: "60px" }}
                    >
                        <h2 style={{ fontSize: "42px", marginBottom: "15px" }}>
                            Frequently Asked Questions
                        </h2>
                        <p style={{ color: "#CBD5E1", lineHeight: "1.8" }}>
                            Everything you need to know before scheduling your consultation.
                        </p>
                    </motion.div>

                    <div style={{ display: "grid", gap: "25px" }}>
                        {[
                            {
                                q: "Is the consultation really free?",
                                a: "Yes. Your first consultation is completely free with no obligation."
                            },
                            {
                                q: "How long is the consultation?",
                                a: "Typically 30–45 minutes depending on your project."
                            },
                            {
                                q: "Which services do you provide?",
                                a: "NetSuite ERP, Salesforce CRM and Web Development."
                            },
                            {
                                q: "Can you work with international clients?",
                                a: "Yes. We provide remote consulting and implementation worldwide."
                            }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ scale: 1.01 }}
                                style={{
                                    background: "rgba(255,255,255,.05)",
                                    border: "1px solid rgba(255,255,255,.08)",
                                    borderRadius: "18px",
                                    padding: "30px",
                                    backdropFilter: "blur(12px)",
                                }}
                            >
                                <h3 style={{ color: "#38BDF8", marginBottom: "12px" }}>
                                    {item.q}
                                </h3>
                                <p style={{ color: "#CBD5E1", lineHeight: "1.8" }}>
                                    {item.a}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* ================= FINAL CTA ================= */}
                <section
                    style={{
                        padding: "80px 8%",
                        marginTop: "80px",
                        background: "linear-gradient(135deg,#2563EB,#06B6D4)",
                        textAlign: "center",
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 style={{ fontSize: "48px", marginBottom: "20px" }}>
                            Ready to Start Your Digital Transformation?
                        </h2>
                        <p
                            style={{
                                maxWidth: "700px",
                                margin: "0 auto 40px",
                                lineHeight: "1.8",
                                fontSize: "18px",
                            }}
                        >
                            Whether you're planning a NetSuite implementation,
                            Salesforce customization, or a custom web application,
                            we're here to help you succeed.
                        </p>
                        <a
                            href="#booking"
                            style={{
                                display: "inline-block",
                                padding: "18px 40px",
                                borderRadius: "50px",
                                background: "#fff",
                                color: "#2563EB",
                                textDecoration: "none",
                                fontWeight: "600",
                                fontSize: "18px",
                            }}
                        >
                            Book Your Free Consultation
                        </a>
                    </motion.div>
                </section>

                {/* ================= CONTACT INFO ================= */}
                <section
                    style={{
                        maxWidth: "1200px",
                        margin: "80px auto",
                        padding: "0 8% 80px",
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))",
                        gap: "30px",
                    }}
                >
                    <div
                        style={{
                            background: "rgba(255,255,255,.05)",
                            padding: "35px",
                            borderRadius: "20px",
                            textAlign: "center",
                        }}
                    >
                        <FaPhoneAlt
                            style={{
                                fontSize: "40px",
                                color: "#38BDF8",
                                marginBottom: "20px",
                            }}
                        />
                        <h3>Call Us</h3>
                        <p style={{ color: "#CBD5E1" }}>+91 74285 68338</p>
                    </div>

                    <div
                        style={{
                            background: "rgba(255,255,255,.05)",
                            padding: "35px",
                            borderRadius: "20px",
                            textAlign: "center",
                        }}
                    >
                        <FaEnvelope
                            style={{
                                fontSize: "40px",
                                color: "#38BDF8",
                                marginBottom: "20px",
                            }}
                        />
                        <h3>Email</h3>
                        <p style={{ color: "#CBD5E1" }}>support@itseva-hub.com</p>
                    </div>

                    <div
                        style={{
                            background: "rgba(255,255,255,.05)",
                            padding: "35px",
                            borderRadius: "20px",
                            textAlign: "center",
                        }}
                    >
                        <FaBuilding
                            style={{
                                fontSize: "40px",
                                color: "#38BDF8",
                                marginBottom: "20px",
                            }}
                        />
                        <h3>Office</h3>
                        <p style={{ color: "#CBD5E1" }}>Gurgaon, Haryana, India</p>
                    </div>
                </section>
            </div>
        </>
    );
}